import React, { useEffect, useRef } from 'react';
import { PendingActions as PendingActionsIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardTitle from '../../../common/card/CardTitle';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineSelect, {
  InlineSelectOptions
} from '../../../common/inline/InlineSelect';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface InventoryAndPendingDispositionCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const dispositionOptions: InlineSelectOptions[] = [
  {
    value: 'n/a',
    label: 'N/A'
  },
  {
    value: 'use to depletion',
    label: 'Use to Depletion'
  },
  { value: 'scarp', label: 'Scrap' },
  { value: 'rework', label: 'Rework' },
  { value: 'rtv', label: 'RTV' }
];

const InventoryAndPendingDispositionCardFields = [
  { name: 'openPo', label: 'Open PO' },
  { name: 'incomingInspection', label: 'Incoming Inspection' },
  { name: 'wip', label: 'WIP' },
  { name: 'fg', label: 'FG' },
  { name: 'quarantineOrHold', label: 'Quarantine/Hold' },
  { name: 'field', label: 'Field' }
];

export const ChangeOrderInventoryAndPendingDispositionCard: React.FC<
  InventoryAndPendingDispositionCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    openPo: changeOrder.openPo || '',
    incomingInspection: changeOrder.incomingInspection || '',
    wip: changeOrder.wip || '',
    fg: changeOrder.fg || '',
    quarantineOrHold: changeOrder.quarantineOrHold || '',
    field: changeOrder.field || '',
    commentsInventory: changeOrder.commentsInventory || '',
    invDueDate: changeOrder.invDueDate,
    invCompletedDate: changeOrder.invCompletedDate
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);
  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <PendingActionsIcon />
          Inventory and Open Order Disposition Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {InventoryAndPendingDispositionCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                options={dispositionOptions}
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='invDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('invDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='invCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('invCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsInventory'
              label='Comments'
              disabled={isFieldDisabled('commentsInventory')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
